/**
 * Merkzettel — Persistenz-Vertrag pro Sitz/User.
 *
 * Klasse MERKZETTEL_SEAT_OR_USER (fail-closed).
 * Polar:
 *   VERBOTEN: fremde Notiz lesen; kanzleiweite Zettelwand; Text in localStorage;
 *             Notiztext im Positions- oder Größen-Key; Außenklick schließt das Fenster;
 *             freies HTML oder Script im Zettel; NC-Notes-UI einbetten;
 *             Notiz an LLM/ARKO; letzter Reiter spurlos ohne leeren Zettel;
 *             React-Kinder oder innerHTML im contentEditable bei jedem Zeichen
 *             (removeChild, Shell weg — Specimen Tippen reißt die Shell);
 *             Markdown-Marker (`*`, `**`, `~~`, `==`) oder rohes HTML im Feld;
 *             Zeilenumbruch, der Marker in die nächste Zeile schreibt;
 *             Markdown-Serialisierung bei jedem Zeichen;
 *             ein zweites Schriftformat, das ein schon gesetztes verwirft
 *             (Kursiv bleibt, Hervorheben kommt dazu — dieselbe Klasse für
 *             jede Kombination der Leiste).
 *   ERLAUBT: N Reiter pro Sitz (title + body + sort); Ausgangsname Zettel 1,
 *            Zettel 2, Umbenennen im Reiterfeld, leerer Name fällt auf die
 *            Nummer zurück; Fenstergröße und
 *            Position nur als Zahlen lokal; Ziehen an Ecken und Kanten,
 *            Mindestmaß für Leiste und ein paar Zeilen, im Viewport bleiben;
 *            schwebendes Fenster am Shell-Host bis X; Markdown-Allowlist im Body,
 *            serialisiert beim Speichern und Blur; fett, kursiv, durchstreichen
 *            und hervorheben gleichzeitig auf derselben Auswahl, Liste zusätzlich;
 *            laufende Schrift bleibt
 *            in der neuen Zeile oder endet sauber, nie als Marker-Text;
 *            NC-Datei unter Sitz/Notizen nur bei echter WebDAV-Session;
 *            sonst PocketBase, ehrlich Handy-Sync später.
 */

export const PORTAL_MERKZETTEL_CLASS = 'MERKZETTEL_SEAT_OR_USER';

export const PORTAL_MERKZETTEL_COLLECTION = 'portal_merkzettel';

export const MERKZETTEL_FOLDER = 'Notizen';

export const MERKZETTEL_FILENAME = 'merkzettel.txt';

export const MERKZETTEL_MAX_CHARS = 20_000;

export const MERKZETTEL_MAX_TITLE = 80;

export const MERKZETTEL_MAX_TABS = 20;

export const MERKZETTEL_DEFAULT_TITLE = 'Zettel';

export const MERKZETTEL_HANDY_SYNC_LATER = 'Abgleich mit dem Handy folgt später';

export const MERKZETTEL_PB_RULE =
  '@request.auth.id != "" && customer_id = @request.auth.id';

/** Nur Fenstergröße — niemals Text. */
export const MERKZETTEL_SIZE_STORAGE_KEY = 'arkorbit-portal-merkzettel-window';

/** Nur Koordinaten — kein Notiztext im Key. */
export const MERKZETTEL_POS_STORAGE_KEY = 'arkorbit-portal-merkzettel-pos';

export const MERKZETTEL_SIZE_MIN = { width: 280, height: 240 };

export const MERKZETTEL_SIZE_MAX = { width: 720, height: 720 };

export const MERKZETTEL_SIZE_DEFAULT = { width: 380, height: 400 };

export type MerkzettelBackend = 'nc' | 'pb';

export type MerkzettelWindowSize = { width: number; height: number };

export type MerkzettelRecord = {
  id?: string;
  customer_id: string;
  kanzlei_id?: string;
  title: string;
  body: string;
  sort: number;
};

export function sanitizeMerkzettelSeatId(raw: string | undefined | null): string {
  return String(raw || '')
    .trim()
    .replace(/[^a-zA-Z0-9_-]/g, '_')
    .substring(0, 80);
}

export function clipMerkzettelBody(raw: string | undefined | null): string {
  return String(raw ?? '').replace(/\r\n/g, '\n').slice(0, MERKZETTEL_MAX_CHARS);
}

/**
 * Tipp-Ereignis (textarea onChange) landet im aktiven Reiter-Body.
 * Index außerhalb → unverändert (kein stilles Schreiben in den falschen Zettel).
 */
export function applyMerkzettelTyping(
  tabs: MerkzettelRecord[],
  activeIndex: number,
  typedValue: string,
): MerkzettelRecord[] {
  if (!Array.isArray(tabs) || activeIndex < 0 || activeIndex >= tabs.length) return tabs;
  const nextBody = clipMerkzettelBody(typedValue);
  return tabs.map((row, i) => (i === activeIndex ? { ...row, body: nextBody } : row));
}

export function merkzettelTabKey(row: MerkzettelRecord, index: number): string {
  return row?.id || `draft:${clipMerkzettelSort(row?.sort)}:${index}`;
}

/**
 * Save merged in die aktuelle Liste — andere Reiter behalten ihren In-Memory-Text.
 */
export function mergeSavedMerkzettelTab(
  current: MerkzettelRecord[],
  saved: MerkzettelRecord,
  savedFromKey: string,
): MerkzettelRecord[] {
  if (!Array.isArray(current) || !saved) return current;
  const fromKey = String(savedFromKey || '');
  let idx = current.findIndex((row, i) => merkzettelTabKey(row, i) === fromKey);
  if (idx < 0 && saved.id) {
    idx = current.findIndex((row) => row.id === saved.id);
  }
  if (idx < 0) return current;
  return current.map((row, i) => (i === idx ? { ...row, ...saved } : row));
}

/**
 * Nach Hintergrund-Save bleibt der geklickte Reiter aktiv.
 * Specimen: persistActive rief applyTabs(preferKey=gespeicherter Zettel) und zog
 * activeKey nach Klick 1→3 wieder auf 1 zurück.
 */
export function resolveMerkzettelActiveKeyAfterSave(args: {
  tabs: MerkzettelRecord[];
  activeKeyBeforeSave: string;
  activeKeyNow: string;
  savedTabKey: string;
}): string {
  const keys = (args.tabs || []).map((row, i) => merkzettelTabKey(row, i));
  const now = String(args.activeKeyNow || '');
  const before = String(args.activeKeyBeforeSave || '');
  const saved = String(args.savedTabKey || '');
  const nowExists = Boolean(now && keys.includes(now));
  const savedExists = Boolean(saved && keys.includes(saved));
  const userSwitched = Boolean(now && now !== before);

  if (userSwitched && nowExists) return now;
  if (!nowExists && savedExists && (now === before || now.startsWith('draft:') || !now)) {
    return saved;
  }
  if (nowExists) return now;
  if (savedExists) return saved;
  return keys[0] || '';
}

/**
 * Dock: Klick setzt activeKey sofort; Save merged ohne preferKey-Steal.
 * VERBOTEN: applyTabs(next, saved.id) nach persistActive; Reiter-Klick erst persist
 * (Save überschreibt die Auswahl). Titel-Input darf mousedown des Nachbar-Reiters
 * nicht schlucken.
 */
export function merkzettelTabClickSelectsClicked(dockSource: string): boolean {
  const s = String(dockSource || '');
  if (!/resolveMerkzettelActiveKeyAfterSave/.test(s)) return false;
  if (!/mergeSavedMerkzettelTab/.test(s)) return false;
  if (/applyTabs\(\s*next\s*,\s*saved/.test(s)) return false;
  if (!/selectMerkzettelTab/.test(s)) return false;
  if (!/e\.preventDefault\(\)/.test(s) || !/onMouseDown/.test(s)) return false;
  return true;
}

/**
 * Schwebendes Fenster: Außenklick schließt nicht. Nur X.
 * Reiter bleiben im Portal nach document.body.
 */
export function merkzettelOutsideClickDoesNotClose(dockSource: string): boolean {
  const dock = String(dockSource || '');
  if (/usePopoverDismissOutside/.test(dock)) return false;
  if (/data-popover-dismiss/.test(dock)) return false;
  if (/addEventListener\(\s*['"]mousedown['"]/.test(dock)) return false;
  if (!/data-testid="portal-merkzettel-close"/.test(dock)) return false;
  if (!/reduceMerkzettelFloat/.test(dock)) return false;
  const portalIdx = dock.indexOf('createPortal');
  const tabIdx = dock.indexOf('data-testid="portal-merkzettel-tab"');
  const bodyIdx = dock.indexOf('document.body', portalIdx);
  if (portalIdx < 0 || tabIdx < 0 || bodyIdx < 0) return false;
  if (!(portalIdx < tabIdx && tabIdx < bodyIdx)) return false;
  return true;
}

/**
 * Host ist die Portal-Shell, nicht ein View der beim Bereichswechsel unmountet.
 */
export function merkzettelWindowHostIsShell(shellSource: string, dockSource: string): boolean {
  const shell = String(shellSource || '');
  const dock = String(dockSource || '');
  const hostIdx = shell.indexOf('data-testid="portal-merkzettel-host"');
  const childrenIdx = shell.indexOf('{children}');
  const dockIdx = shell.indexOf('<MerkzettelDock');
  if (hostIdx < 0 || childrenIdx < 0 || dockIdx < 0) return false;
  if (!(childrenIdx < hostIdx && hostIdx < dockIdx)) return false;
  if (/<MerkzettelDock[\s\S]{0,240}key=\{view\}/.test(shell)) return false;
  if (shell.includes("view === 'merkzettel'")) return false;
  if (/usePopoverDismissOutside/.test(dock)) return false;
  if (!/createPortal/.test(dock) || !/document\.body/.test(dock)) return false;
  if (!/\bfixed\b/.test(dock)) return false;
  if (!/reduceMerkzettelFloat/.test(dock)) return false;
  if (!/MERKZETTEL_POS_STORAGE_KEY/.test(dock)) return false;
  if (!/portal-merkzettel-format-\$\{id\}/.test(dock)) return false;
  if (!/id: 'bold'/.test(dock)) return false;
  if (/dangerouslySetInnerHTML/.test(dock)) return false;
  return true;
}

/**
 * Editor-Hit-Target: Body muss die Fläche füllen. Resize nur Ecke, Gutter ohne Pointer.
 * Specimen „Resize-Overlay frisst Tastatur“.
 */
export function merkzettelTypingTargetIsBody(dockSource: string): boolean {
  const s = String(dockSource || '');
  const bodyIdx = s.indexOf('data-testid="portal-merkzettel-body"');
  const gutterIdx = s.indexOf('data-testid="portal-merkzettel-gutter"');
  const resizeIdx = s.indexOf('data-testid="portal-merkzettel-resize"');
  if (bodyIdx < 0 || gutterIdx < 0 || resizeIdx < 0) return false;
  const bodyChunk = s.slice(bodyIdx, gutterIdx > bodyIdx ? gutterIdx : bodyIdx + 700);
  const gutterChunk = s.slice(gutterIdx, gutterIdx + 420);
  const resizeChunk = s.slice(resizeIdx, resizeIdx + 420);
  if (/\breadOnly\b/.test(bodyChunk) || /\bdisabled\b/.test(bodyChunk)) return false;
  if (/dangerouslySetInnerHTML/.test(s)) return false;
  if (!/\bcontentEditable\b/.test(bodyChunk)) return false;
  if (/<textarea\b/.test(bodyChunk)) return false;
  if (!/buildMerkzettelView/.test(s)) return false;
  if (!/\binset-0\b/.test(bodyChunk)) return false;
  if (!/\bpointer-events-none\b/.test(gutterChunk)) return false;
  if (/\binset-0\b/.test(resizeChunk)) return false;
  if (!/\bh-4\b/.test(resizeChunk) || !/\bw-4\b/.test(resizeChunk)) return false;
  if (/onKeyDown/.test(bodyChunk) && /preventDefault/.test(bodyChunk)) return false;
  return true;
}

export type MerkzettelEditorOrigin = 'input' | 'structure' | 'external';

/**
 * Tippen besitzt den contentEditable-Knoten.
 * React malt ihn nicht bei jedem Zeichen neu — sonst removeChild und die Shell ist weg.
 * Malen bleibt bei Format, Enter, Einfügen, Reiterwechsel und wenn das Feld nicht fokussiert ist.
 */
export function merkzettelShouldPaintEditor(args: {
  focused: boolean;
  origin: MerkzettelEditorOrigin;
}): boolean {
  if (args.origin === 'input' && args.focused) return false;
  return true;
}

/**
 * Quelle: keine React-Kinder zwischen Body und Gutter.
 * Specimen Tippen reißt die Shell.
 */
export function merkzettelTypingKeepsShell(dockSource: string): boolean {
  const s = String(dockSource || '');
  const bodyIdx = s.indexOf('data-testid="portal-merkzettel-body"');
  const gutterIdx = s.indexOf('data-testid="portal-merkzettel-gutter"');
  if (bodyIdx < 0 || gutterIdx <= bodyIdx) return false;
  const chunk = s.slice(bodyIdx, gutterIdx);
  if (/MerkzettelEditorBlocks/.test(chunk)) return false;
  if (/dangerouslySetInnerHTML/.test(chunk)) return false;
  if (/<textarea\b/.test(chunk)) return false;
  if (!/\bcontentEditable\b/.test(chunk)) return false;
  if (!/editorOwnsDomRef/.test(s)) return false;
  if (!/merkzettelShouldPaintEditor/.test(s)) return false;
  if (!/paintMerkzettelEditor/.test(s)) return false;
  return true;
}

export function clipMerkzettelTitle(
  raw: string | undefined | null,
  opts?: { trim?: boolean },
): string {
  let s = String(raw ?? '').replace(/[\r\n]/g, ' ');
  if (opts?.trim !== false) s = s.replace(/\s+/g, ' ').trim();
  return s.slice(0, MERKZETTEL_MAX_TITLE);
}

export function clipMerkzettelSort(raw: unknown): number {
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(Math.floor(n), 9999);
}

export function displayMerkzettelTitle(title: string | undefined | null, index: number): string {
  const t = clipMerkzettelTitle(title);
  if (t) return t;
  return `${MERKZETTEL_DEFAULT_TITLE} ${Math.max(0, index) + 1}`;
}

export function countMerkzettelLines(body: string | undefined | null): number {
  const text = String(body ?? '');
  if (!text) return 1;
  let n = 1;
  for (let i = 0; i < text.length; i += 1) {
    if (text[i] === '\n') n += 1;
  }
  return n;
}

export function sortMerkzettelTabs(tabs: MerkzettelRecord[]): MerkzettelRecord[] {
  return [...tabs].sort((a, b) => {
    const ds = clipMerkzettelSort(a.sort) - clipMerkzettelSort(b.sort);
    if (ds !== 0) return ds;
    return String(a.id || '').localeCompare(String(b.id || ''));
  });
}

export function emptyMerkzettelTab(seatId: string, sort = 0): MerkzettelRecord {
  return {
    customer_id: sanitizeMerkzettelSeatId(seatId),
    title: '',
    body: '',
    sort: clipMerkzettelSort(sort),
  };
}

export function ensureMerkzettelTabs(
  tabs: MerkzettelRecord[],
  seatId: string,
): MerkzettelRecord[] {
  const cid = sanitizeMerkzettelSeatId(seatId);
  const mine = sortMerkzettelTabs(
    tabs.filter((row) => merkzettelRecordVisibleToSeat(row, cid)),
  );
  if (!cid) return [];
  if (mine.length === 0) return [emptyMerkzettelTab(cid, 0)];
  return mine;
}

export function nextMerkzettelSort(tabs: MerkzettelRecord[]): number {
  if (!tabs.length) return 0;
  return Math.max(...tabs.map((row) => clipMerkzettelSort(row.sort))) + 1;
}

export function canAddMerkzettelTab(tabs: MerkzettelRecord[]): boolean {
  return tabs.length < MERKZETTEL_MAX_TABS;
}

export function planMerkzettelTabDelete(tabCount: number): 'delete' | 'clear-last' {
  return tabCount <= 1 ? 'clear-last' : 'delete';
}

export function clipMerkzettelWindowSize(
  size: { width?: unknown; height?: unknown },
  viewport?: { width: number; height: number },
): MerkzettelWindowSize {
  const viewW = viewport ? Math.max(1, viewport.width - 16) : MERKZETTEL_SIZE_MAX.width;
  const viewH = viewport ? Math.max(1, viewport.height - 16) : MERKZETTEL_SIZE_MAX.height;
  const maxW = Math.min(MERKZETTEL_SIZE_MAX.width, viewW);
  const maxH = Math.min(MERKZETTEL_SIZE_MAX.height, viewH);
  const minW = Math.min(MERKZETTEL_SIZE_MIN.width, maxW);
  const minH = Math.min(MERKZETTEL_SIZE_MIN.height, maxH);
  const width = Number(size.width);
  const height = Number(size.height);
  return {
    width: Math.min(maxW, Math.max(minW, Number.isFinite(width) ? Math.round(width) : MERKZETTEL_SIZE_DEFAULT.width)),
    height: Math.min(maxH, Math.max(minH, Number.isFinite(height) ? Math.round(height) : MERKZETTEL_SIZE_DEFAULT.height)),
  };
}

/**
 * Fenstergröße aus localStorage. Textfelder im JSON → verwerfen (kein Body-Store).
 */
export function parseMerkzettelWindowSize(raw: string | null | undefined): MerkzettelWindowSize | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    const v = parsed as Record<string, unknown>;
    if ('body' in v || 'title' in v || 'tabs' in v || 'customer_id' in v) return null;
    if (typeof v.width !== 'number' || typeof v.height !== 'number') return null;
    return clipMerkzettelWindowSize({ width: v.width, height: v.height });
  } catch {
    return null;
  }
}

export function serializeMerkzettelWindowSize(size: MerkzettelWindowSize): string {
  const clipped = clipMerkzettelWindowSize(size);
  return JSON.stringify({ width: clipped.width, height: clipped.height });
}

export type MerkzettelResizeEdge = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

function merkzettelResizeTouches(edge: MerkzettelResizeEdge, flag: 'n' | 's' | 'e' | 'w'): boolean {
  return edge.includes(flag);
}

/**
 * Ecken diagonal, Kanten nur in ihrer Achse. Gegenüberliegende Kante bleibt.
 * Mindestmaß lässt Leiste und ein paar Zeilen. Das Rechteck bleibt im Viewport.
 * Rückgabe nur Zahlen — kein Notiztext.
 */
export function resizeMerkzettelWindow(args: {
  left: number;
  top: number;
  width: number;
  height: number;
  dx: number;
  dy: number;
  edge: MerkzettelResizeEdge;
  viewport: { width: number; height: number };
}): { left: number; top: number; width: number; height: number } {
  const margin = 8;
  const vp = args.viewport;
  const startRight = args.left + args.width;
  const startBottom = args.top + args.height;
  let left = args.left;
  let top = args.top;
  let width = args.width;
  let height = args.height;
  if (merkzettelResizeTouches(args.edge, 'e')) width += args.dx;
  if (merkzettelResizeTouches(args.edge, 'w')) width -= args.dx;
  if (merkzettelResizeTouches(args.edge, 's')) height += args.dy;
  if (merkzettelResizeTouches(args.edge, 'n')) height -= args.dy;
  const sized = clipMerkzettelWindowSize({ width, height }, vp);
  width = sized.width;
  height = sized.height;
  if (merkzettelResizeTouches(args.edge, 'w')) left = startRight - width;
  if (merkzettelResizeTouches(args.edge, 'n')) top = startBottom - height;
  if (merkzettelResizeTouches(args.edge, 'w') && left < margin) {
    left = margin;
    width = clipMerkzettelWindowSize({ width: startRight - left, height }, vp).width;
    left = startRight - width;
  }
  if (merkzettelResizeTouches(args.edge, 'n') && top < margin) {
    top = margin;
    height = clipMerkzettelWindowSize({ width, height: startBottom - top }, vp).height;
    top = startBottom - height;
  }
  if (
    merkzettelResizeTouches(args.edge, 'e') &&
    !merkzettelResizeTouches(args.edge, 'w') &&
    left + width > vp.width - margin
  ) {
    width = clipMerkzettelWindowSize({ width: vp.width - margin - left, height }, vp).width;
  }
  if (
    merkzettelResizeTouches(args.edge, 's') &&
    !merkzettelResizeTouches(args.edge, 'n') &&
    top + height > vp.height - margin
  ) {
    height = clipMerkzettelWindowSize({ width, height: vp.height - margin - top }, vp).height;
  }
  const pos = clipMerkzettelWindowPosition({ left, top }, { width, height }, vp);
  return { left: pos.left, top: pos.top, width, height };
}

export function placeMerkzettelPopover(
  anchor: { left: number; top: number; right: number; bottom: number },
  viewport: { width: number; height: number },
  size: MerkzettelWindowSize,
): { left: number; top: number } {
  const gap = 8;
  const margin = 8;
  const clipped = clipMerkzettelWindowSize(size, viewport);
  let left = anchor.right + gap;
  const maxLeft = Math.max(margin, viewport.width - clipped.width - margin);
  if (left > maxLeft) left = maxLeft;
  if (left < margin) left = margin;
  let top = anchor.top;
  const maxTop = Math.max(margin, viewport.height - clipped.height - margin);
  top = Math.min(Math.max(top, margin), maxTop);
  return { left, top };
}

export type MerkzettelWindowPos = { left: number; top: number };

const MERKZETTEL_POS_FORBIDDEN_KEYS = [
  'body',
  'title',
  'tabs',
  'customer_id',
  'kanzlei_id',
  'text',
  'note',
  'html',
  'script',
  'markdown',
];

/**
 * Position bleibt im Viewport. Größe kommt getrennt; hier nur left/top.
 */
export function clipMerkzettelWindowPosition(
  pos: { left?: unknown; top?: unknown },
  size: MerkzettelWindowSize,
  viewport?: { width: number; height: number },
): MerkzettelWindowPos {
  const margin = 8;
  const vw = viewport && viewport.width > 0 ? viewport.width : 1280;
  const vh = viewport && viewport.height > 0 ? viewport.height : 800;
  const width = clipMerkzettelWindowSize(size, { width: vw, height: vh }).width;
  const height = clipMerkzettelWindowSize(size, { width: vw, height: vh }).height;
  const maxLeft = Math.max(margin, vw - width - margin);
  const maxTop = Math.max(margin, vh - height - margin);
  const leftN = Number(pos.left);
  const topN = Number(pos.top);
  return {
    left: Math.min(maxLeft, Math.max(margin, Number.isFinite(leftN) ? Math.round(leftN) : margin)),
    top: Math.min(maxTop, Math.max(margin, Number.isFinite(topN) ? Math.round(topN) : margin)),
  };
}

/**
 * Koordinaten aus localStorage. Notizfelder im JSON → verwerfen.
 */
export function parseMerkzettelWindowPosition(raw: string | null | undefined): MerkzettelWindowPos | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    const v = parsed as Record<string, unknown>;
    if (MERKZETTEL_POS_FORBIDDEN_KEYS.some((key) => key in v)) return null;
    if (typeof v.left !== 'number' || typeof v.top !== 'number') return null;
    if (!Number.isFinite(v.left) || !Number.isFinite(v.top)) return null;
    return { left: Math.round(v.left), top: Math.round(v.top) };
  } catch {
    return null;
  }
}

export function serializeMerkzettelWindowPosition(pos: MerkzettelWindowPos): string {
  const left = Math.round(Number(pos.left));
  const top = Math.round(Number(pos.top));
  return JSON.stringify({
    left: Number.isFinite(left) ? left : 8,
    top: Number.isFinite(top) ? top : 8,
  });
}

export type MerkzettelFloatState = {
  open: boolean;
  left: number;
  top: number;
};

export type MerkzettelFloatAction =
  | { type: 'view' }
  | { type: 'outside' }
  | { type: 'close' }
  | { type: 'open'; left: number; top: number }
  | { type: 'move'; left: number; top: number };

/**
 * View-Wechsel und Außenklick lassen das Fenster stehen.
 * Schließen nur über close (X). move/open ändern die Koordinaten.
 */
export function reduceMerkzettelFloat(
  state: MerkzettelFloatState,
  action: MerkzettelFloatAction,
): MerkzettelFloatState {
  if (action.type === 'view' || action.type === 'outside') return state;
  if (action.type === 'close') {
    return state.open ? { ...state, open: false } : state;
  }
  if (action.type === 'open') {
    return { open: true, left: action.left, top: action.top };
  }
  if (action.type === 'move') {
    return { ...state, left: action.left, top: action.top };
  }
  return state;
}

export type MerkzettelFormat =
  | 'bold'
  | 'italic'
  | 'strike'
  | 'highlight'
  | 'bullet'
  | 'number';

export type MerkzettelMark = 'bold' | 'italic' | 'strike' | 'highlight';

export type MerkzettelInlineKind = 'text' | MerkzettelMark;

export type MerkzettelInline = {
  kind: MerkzettelInlineKind;
  /** Alle Schriftformate dieser Spanne. Leer = Klartext. */
  marks: MerkzettelMark[];
  text: string;
  /** Start des sichtbaren Textes im Markdown, Marker nicht mitgezählt. */
  mdStart: number;
  /** Ende des sichtbaren Textes, exklusiv. */
  mdEnd: number;
};

export type MerkzettelBlock =
  | { kind: 'line'; inlines: MerkzettelInline[] }
  | { kind: 'bullet'; inlines: MerkzettelInline[] }
  | { kind: 'number'; n: number; inlines: MerkzettelInline[] };

const INLINE_MARK: Record<MerkzettelMark, readonly [string, string]> = {
  bold: ['**', '**'],
  italic: ['*', '*'],
  strike: ['~~', '~~'],
  highlight: ['==', '=='],
};

/** Außen nach innen. Kursiv innen, damit `*` nicht `**` frisst. */
export const MERKZETTEL_MARK_ORDER: readonly MerkzettelMark[] = ['highlight', 'strike', 'bold', 'italic'];

function canonicalMarks(marks: readonly MerkzettelMark[]): MerkzettelMark[] {
  const set = new Set(marks);
  return MERKZETTEL_MARK_ORDER.filter((mark) => set.has(mark));
}

function deriveKind(marks: readonly MerkzettelMark[]): MerkzettelInlineKind {
  const ordered = canonicalMarks(marks);
  if (ordered.length === 0) return 'text';
  if (ordered.length === 1) return ordered[0];
  return ordered[0];
}

function spanMarks(span: MerkzettelInline): MerkzettelMark[] {
  if (Array.isArray(span.marks) && span.marks.length) return canonicalMarks(span.marks);
  if (span.kind !== 'text') return [span.kind];
  return [];
}

function withMark(span: MerkzettelInline, mark: MerkzettelMark): MerkzettelInline {
  const marks = canonicalMarks([...spanMarks(span), mark]);
  return { ...span, marks, kind: deriveKind(marks) };
}

function wrapLengths(span: MerkzettelInline): { open: number; close: number } {
  let open = 0;
  let close = 0;
  for (const mark of spanMarks(span)) {
    open += INLINE_MARK[mark][0].length;
    close += INLINE_MARK[mark][1].length;
  }
  return { open, close };
}

const BLOCK_KINDS = new Set(['line', 'bullet', 'number']);
const INLINE_KINDS = new Set(['text', 'bold', 'italic', 'strike', 'highlight']);

function clampMerkzettelSelection(body: string, start: number, end: number): [number, number] {
  const len = body.length;
  const a = Math.max(0, Math.min(len, Number.isFinite(start) ? Math.floor(start) : 0));
  const b = Math.max(0, Math.min(len, Number.isFinite(end) ? Math.floor(end) : a));
  return a <= b ? [a, b] : [b, a];
}

function visibleLengthOf(block: MerkzettelBlock): number {
  return block.inlines.reduce((sum, span) => sum + span.text.length, 0);
}

function toggleMerkzettelInlines(
  inlines: MerkzettelInline[],
  from: number,
  to: number,
  format: MerkzettelMark,
  remove: boolean,
): MerkzettelInline[] {
  const chars: Array<{ ch: string; marks: MerkzettelMark[] }> = [];
  for (const span of inlines) {
    const marks = spanMarks(span);
    for (const ch of span.text) chars.push({ ch, marks: [...marks] });
  }
  const end = Math.min(to, chars.length);
  for (let i = Math.max(0, from); i < end; i += 1) {
    const set = new Set(chars[i].marks);
    if (remove) set.delete(format);
    else set.add(format);
    chars[i].marks = canonicalMarks([...set]);
  }
  const out: MerkzettelInline[] = [];
  for (const item of chars) {
    const last = out[out.length - 1];
    const key = item.marks.join('\0');
    if (last && last.marks.join('\0') === key) {
      last.text += item.ch;
      continue;
    }
    out.push({
      kind: deriveKind(item.marks),
      marks: item.marks,
      text: item.ch,
      mdStart: 0,
      mdEnd: 0,
    });
  }
  return out;
}

/**
 * Schriftformat auf die sichtbare Auswahl legen oder abnehmen.
 * Andere Formate derselben Spanne bleiben. Marker stehen nie im Text.
 */
function applyMerkzettelInlineFormat(
  body: string,
  start: number,
  end: number,
  format: MerkzettelMark,
): { body: string; start: number; end: number } {
  if (start === end) {
    const [open, close] = INLINE_MARK[format];
    const next = clipMerkzettelBody(body.slice(0, start) + open + close + body.slice(end));
    const caret = Math.min(start + open.length, next.length);
    return { body: next, start: caret, end: caret };
  }
  const view = buildMerkzettelView(body);
  const blocks = viewBlocksAsModel(view);
  if (!blocks.length) {
    return { body, start, end };
  }
  const locA = merkzettelLocateOffset(body, start);
  const locB = merkzettelLocateOffset(body, end);
  const blockA = Math.max(0, Math.min(locA.block, blocks.length - 1));
  const blockB = Math.max(0, Math.min(locB.block, blocks.length - 1));
  const visA = visibleOffsetInBlock(blocks[blockA], locA.inline, locA.char);
  const visB = visibleOffsetInBlock(blocks[blockB], locB.inline, locB.char);
  const ranges = blocks.map((block, index) => {
    if (index < blockA || index > blockB) return { from: 0, to: 0 };
    const from = index === blockA ? visA : 0;
    const to = index === blockB ? visB : visibleLengthOf(block);
    return { from, to: Math.max(from, to) };
  });
  let any = false;
  let allHave = true;
  blocks.forEach((block, index) => {
    const { from, to } = ranges[index];
    if (from >= to) return;
    let seen = 0;
    for (const span of block.inlines) {
      const next = seen + span.text.length;
      if (Math.min(to, next) > Math.max(from, seen)) {
        any = true;
        if (!spanMarks(span).includes(format)) allHave = false;
      }
      seen = next;
    }
  });
  if (!any) return { body, start, end };
  const nextBlocks = blocks.map((block, index) => {
    const { from, to } = ranges[index];
    if (from >= to) return block;
    return replaceBlockInlines(block, toggleMerkzettelInlines(block.inlines, from, to, format, allHave));
  });
  const nextBody = clipMerkzettelBody(serializeMerkzettelMarkdown(nextBlocks));
  return {
    body: nextBody,
    start: Math.min(caretInBlock(nextBlocks, blockA, visA), nextBody.length),
    end: Math.min(caretInBlock(nextBlocks, blockB, visB), nextBody.length),
  };
}

function applyMerkzettelListFormat(
  body: string,
  start: number,
  end: number,
  format: 'bullet' | 'number',
): { body: string; start: number; end: number } {
  const from = body.lastIndexOf('\n', Math.max(0, start - 1)) + 1;
  let cursor = end;
  if (cursor > from && body[cursor - 1] === '\n') cursor -= 1;
  const lineEndIdx = body.indexOf('\n', cursor);
  const until = lineEndIdx < 0 ? body.length : lineEndIdx;
  const lines = body.slice(from, until).split('\n');
  const bulletRe = /^- /;
  const numberRe = /^\d+\. /;
  const nonempty = lines.filter((line) => line.length > 0);
  const allOn =
    format === 'bullet'
      ? nonempty.length > 0 && nonempty.every((line) => bulletRe.test(line))
      : nonempty.length > 0 && nonempty.every((line) => numberRe.test(line));
  let n = 1;
  const nextLines = lines.map((line) => {
    if (allOn) {
      if (format === 'bullet') return line.replace(/^- /, '');
      return line.replace(/^\d+\. /, '');
    }
    if (format === 'bullet') {
      if (bulletRe.test(line)) return line;
      if (!line.length) return '- ';
      return `- ${line}`;
    }
    if (!line.length) {
      const prefixed = `${n}. `;
      n += 1;
      return prefixed;
    }
    const stripped = line.replace(/^\d+\. /, '');
    const prefixed = `${n}. ${stripped}`;
    n += 1;
    return prefixed;
  });
  const nextChunk = nextLines.join('\n');
  const nextBody = clipMerkzettelBody(body.slice(0, from) + nextChunk + body.slice(until));
  return {
    body: nextBody,
    start: from,
    end: Math.min(nextBody.length, from + nextChunk.length),
  };
}

export function applyMerkzettelFormat(
  raw: string,
  start: number,
  end: number,
  format: MerkzettelFormat,
): { body: string; start: number; end: number } {
  const body = clipMerkzettelBody(raw);
  const [s, e] = clampMerkzettelSelection(body, start, end);
  if (format === 'bullet' || format === 'number') {
    return applyMerkzettelListFormat(body, s, e, format);
  }
  return applyMerkzettelInlineFormat(body, s, e, format);
}

const MERKZETTEL_WORD_EDGE = /[\s*~=]/;

/**
 * Kollabierte Auswahl im Wort wird zum Wort. Marker `*~=` stoppen,
 * damit `**Hinweis**` nicht noch einmal eingepackt wird.
 * Leere Stelle bleibt kollabiert (dann setzt der Editor eine echte Schrift, keine `"`).
 */
export function expandMerkzettelSelectionToWord(
  body: string,
  start: number,
  end: number,
): [number, number] {
  const text = String(body ?? '');
  if (start !== end) return [start, end];
  const edge = (i: number) => i < 0 || i >= text.length || MERKZETTEL_WORD_EDGE.test(text[i]);
  if (edge(start - 1) && edge(start)) return [start, end];
  let a = start;
  let b = start;
  while (a > 0 && !MERKZETTEL_WORD_EDGE.test(text[a - 1])) a -= 1;
  while (b < text.length && !MERKZETTEL_WORD_EDGE.test(text[b])) b += 1;
  if (a === b) return [start, end];
  return [a, b];
}

export function merkzettelSelectionWrappedInQuotes(
  raw: string,
  start: number,
  end: number,
  format: MerkzettelFormat,
): boolean {
  const next = applyMerkzettelFormat(raw, start, end, format).body;
  const selected = String(raw ?? '').slice(Math.max(0, Math.min(start, end)), Math.max(start, end));
  if (!selected) return false;
  return next.includes(`"${selected}"`);
}

const INLINE_SCAN: ReadonlyArray<{ kind: 'bold' | 'strike' | 'highlight' | 'italic'; open: string; close: string }> = [
  { kind: 'bold', open: '**', close: '**' },
  { kind: 'strike', open: '~~', close: '~~' },
  { kind: 'highlight', open: '==', close: '==' },
  { kind: 'italic', open: '*', close: '*' },
];

function parseMerkzettelInlines(input: string, base = 0): MerkzettelInline[] {
  const out: MerkzettelInline[] = [];
  let i = 0;
  let buf = '';
  let bufAt = 0;
  const flush = () => {
    if (!buf) return;
    out.push({
      kind: 'text',
      marks: [],
      text: buf,
      mdStart: base + bufAt,
      mdEnd: base + bufAt + buf.length,
    });
    buf = '';
  };
  while (i < input.length) {
    let matched = false;
    if (input.startsWith('***', i)) {
      const closeAt = input.indexOf('***', i + 3);
      const inner = closeAt > i + 3 ? input.slice(i + 3, closeAt) : '';
      if (closeAt > i + 3 && !inner.includes('\n')) {
        flush();
        const nested = parseMerkzettelInlines(inner, base + i + 3);
        const stamped = nested
          .filter((span) => span.text.length > 0)
          .map((span) => withMark(withMark(span, 'italic'), 'bold'));
        if (stamped.length) out.push(...stamped);
        else {
          out.push({
            kind: 'text',
            marks: [],
            text: input.slice(i, closeAt + 3),
            mdStart: base + i,
            mdEnd: base + closeAt + 3,
          });
        }
        i = closeAt + 3;
        matched = true;
      }
    }
    if (matched) continue;
    for (const mark of INLINE_SCAN) {
      if (!input.startsWith(mark.open, i)) continue;
      if (mark.kind === 'italic' && input[i + 1] === '*') continue;
      const closeAt = input.indexOf(mark.close, i + mark.open.length);
      if (closeAt <= i + mark.open.length) continue;
      if (mark.kind === 'italic' && input[closeAt + 1] === '*') continue;
      const inner = input.slice(i + mark.open.length, closeAt);
      if (inner.includes('\n')) continue;
      flush();
      const nested = parseMerkzettelInlines(inner, base + i + mark.open.length);
      const stamped = nested
        .filter((span) => span.text.length > 0)
        .map((span) => withMark(span, mark.kind));
      if (stamped.length) out.push(...stamped);
      else {
        const literal = mark.open + inner + mark.close;
        out.push({
          kind: 'text',
          marks: [],
          text: literal,
          mdStart: base + i,
          mdEnd: base + i + literal.length,
        });
      }
      i = closeAt + mark.close.length;
      matched = true;
      break;
    }
    if (!matched) {
      if (!buf) bufAt = i;
      buf += input[i];
      i += 1;
    }
  }
  flush();
  return out;
}

/**
 * Allowlist: fett, kursiv, durchstreichen, hervorheben, Aufzählung, Nummerierung.
 * HTML und Script bleiben Zeichen, keine Knoten.
 */
export function parseMerkzettelMarkdown(raw: string): MerkzettelBlock[] {
  const text = String(raw ?? '').replace(/\r\n/g, '\n');
  if (!text) return [];
  return text.split('\n').map((line) => {
    const num = /^(\d+)\. (.*)$/.exec(line);
    if (num) {
      return { kind: 'number' as const, n: Number(num[1]), inlines: parseMerkzettelInlines(num[2]) };
    }
    if (line.startsWith('- ')) {
      return { kind: 'bullet' as const, inlines: parseMerkzettelInlines(line.slice(2)) };
    }
    return { kind: 'line' as const, inlines: parseMerkzettelInlines(line) };
  });
}

export type MerkzettelViewBlock = {
  kind: 'line' | 'bullet' | 'number';
  n?: number;
  mdStart: number;
  mdEnd: number;
  contentStart: number;
  inlines: MerkzettelInline[];
};

export type MerkzettelArmedMark = {
  kind: MerkzettelMark;
  marks?: MerkzettelMark[];
  at: number;
};

export function buildMerkzettelView(raw: string): MerkzettelViewBlock[] {
  const text = String(raw ?? '').replace(/\r\n/g, '\n');
  if (!text) return [];
  const lines = text.split('\n');
  let offset = 0;
  const blocks: MerkzettelViewBlock[] = [];
  for (const line of lines) {
    const mdStart = offset;
    const num = /^(\d+)\. (.*)$/.exec(line);
    let kind: MerkzettelViewBlock['kind'] = 'line';
    let n: number | undefined;
    let content = line;
    let contentStart = mdStart;
    if (num) {
      kind = 'number';
      n = Number(num[1]);
      content = num[2];
      contentStart = mdStart + num[1].length + 2;
    } else if (line.startsWith('- ')) {
      kind = 'bullet';
      content = line.slice(2);
      contentStart = mdStart + 2;
    }
    blocks.push({
      kind,
      n,
      mdStart,
      mdEnd: mdStart + line.length,
      contentStart,
      inlines: parseMerkzettelInlines(content, contentStart),
    });
    offset += line.length + 1;
  }
  return blocks;
}

/** Sichtbarer Text: Marker und `"`-Hüllen aus der Leiste sind keine Schrift. */
export function merkzettelVisiblePlain(raw: string): string {
  const text = String(raw ?? '').replace(/\r\n/g, '\n');
  if (!text) return '';
  return buildMerkzettelView(text)
    .map((block) => {
      const inner = block.inlines.map((span) => span.text.replace(/\u200b/g, '')).join('');
      if (block.kind === 'bullet') return `• ${inner}`;
      if (block.kind === 'number') return `${block.n}. ${inner}`;
      return inner;
    })
    .join('\n');
}

function merkzettelViewEnd(
  block: MerkzettelViewBlock,
  blockIndex: number,
): { block: number; inline: number; char: number } {
  const last = block.inlines.length - 1;
  if (last < 0) return { block: blockIndex, inline: -1, char: 0 };
  return { block: blockIndex, inline: last, char: block.inlines[last].text.length };
}

export function merkzettelLocateOffset(
  raw: string,
  index: number,
): { block: number; inline: number; char: number } {
  const text = String(raw ?? '').replace(/\r\n/g, '\n');
  const view = buildMerkzettelView(text);
  if (!view.length) return { block: 0, inline: -1, char: 0 };
  const clamped = Math.max(0, Math.min(index, text.length));
  for (let b = 0; b < view.length; b += 1) {
    const block = view[b];
    const nextStart = b + 1 < view.length ? view[b + 1].mdStart : text.length;
    if (clamped > block.mdEnd && clamped < nextStart) return merkzettelViewEnd(block, b);
    if (clamped < block.mdStart || clamped > block.mdEnd) continue;
    if (!block.inlines.length || clamped <= block.contentStart) {
      return { block: b, inline: block.inlines.length ? 0 : -1, char: 0 };
    }
    for (let i = 0; i < block.inlines.length; i += 1) {
      const span = block.inlines[i];
      const { open, close } = wrapLengths(span);
      if (clamped >= span.mdStart - open && clamped <= span.mdEnd + close) {
        const char = Math.max(0, Math.min(span.text.length, clamped - span.mdStart));
        return { block: b, inline: i, char };
      }
    }
    return merkzettelViewEnd(block, b);
  }
  return merkzettelViewEnd(view[view.length - 1], view.length - 1);
}

export type MerkzettelLineBreak = {
  body: string;
  caret: number;
  /** Eine Schrift, wenn genau ein Format weiter gilt. Kein Marker im Text. */
  continueKind: MerkzettelArmedMark['kind'] | null;
  /** Alle Schriften, die auf der neuen Zeile weiter gelten. */
  continueMarks: MerkzettelMark[];
};

function cleanMerkzettelInlines(spans: MerkzettelInline[]): MerkzettelInline[] {
  return spans
    .map((span) => {
      const marks = spanMarks(span);
      return {
        kind: deriveKind(marks),
        marks,
        text: span.text.replace(/\u200b/g, ''),
        mdStart: 0,
        mdEnd: 0,
      };
    })
    .filter((span) => span.text.length > 0);
}

function splitMerkzettelInlines(
  inlines: MerkzettelInline[],
  at: number,
): { left: MerkzettelInline[]; right: MerkzettelInline[]; kindAt: MerkzettelInlineKind; marksAt: MerkzettelMark[] } {
  const left: MerkzettelInline[] = [];
  const right: MerkzettelInline[] = [];
  let seen = 0;
  let kindAt: MerkzettelInlineKind = 'text';
  let marksAt: MerkzettelMark[] = [];
  let placed = false;
  for (const span of inlines) {
    const text = span.text.replace(/\u200b/g, '');
    const marks = spanMarks(span);
    if (placed) {
      if (text) right.push({ ...span, marks, kind: deriveKind(marks), text, mdStart: 0, mdEnd: 0 });
      continue;
    }
    if (at > seen + text.length) {
      if (text) left.push({ ...span, marks, kind: deriveKind(marks), text, mdStart: 0, mdEnd: 0 });
      seen += text.length;
      kindAt = deriveKind(marks);
      marksAt = marks;
      continue;
    }
    const cut = Math.max(0, at - seen);
    kindAt = deriveKind(marks);
    marksAt = marks;
    const head = text.slice(0, cut);
    const tail = text.slice(cut);
    if (head) left.push({ ...span, marks, kind: deriveKind(marks), text: head, mdStart: 0, mdEnd: 0 });
    if (tail) right.push({ ...span, marks, kind: deriveKind(marks), text: tail, mdStart: 0, mdEnd: 0 });
    placed = true;
  }
  return { left, right, kindAt, marksAt };
}

function viewBlocksAsModel(view: MerkzettelViewBlock[]): MerkzettelBlock[] {
  return view.map((block) => {
    const inlines = cleanMerkzettelInlines(block.inlines);
    if (block.kind === 'bullet') return { kind: 'bullet', inlines };
    if (block.kind === 'number') return { kind: 'number', n: block.n || 1, inlines };
    return { kind: 'line', inlines };
  });
}

function replaceBlockInlines(block: MerkzettelBlock, inlines: MerkzettelInline[]): MerkzettelBlock {
  const clean = cleanMerkzettelInlines(inlines);
  if (block.kind === 'number') return { kind: 'number', n: block.n, inlines: clean };
  if (block.kind === 'bullet') return { kind: 'bullet', inlines: clean };
  return { kind: 'line', inlines: clean };
}

function renumberNumberRun(blocks: MerkzettelBlock[], index: number): MerkzettelBlock[] {
  if (blocks[index]?.kind !== 'number') return blocks;
  let start = index;
  while (start > 0 && blocks[start - 1].kind === 'number') start -= 1;
  let end = index;
  while (end + 1 < blocks.length && blocks[end + 1].kind === 'number') end += 1;
  const out = blocks.slice();
  let n = 1;
  for (let i = start; i <= end; i += 1) {
    const block = out[i];
    if (block.kind === 'number') {
      out[i] = { ...block, n };
      n += 1;
    }
  }
  return out;
}

function visibleOffsetInBlock(block: MerkzettelBlock, inline: number, char: number): number {
  if (inline < 0) return 0;
  let seen = 0;
  for (let i = 0; i < block.inlines.length; i += 1) {
    if (i === inline) return seen + Math.max(0, Math.min(char, block.inlines[i].text.length));
    seen += block.inlines[i].text.length;
  }
  return seen;
}

function caretInBlock(blocks: MerkzettelBlock[], blockIndex: number, visibleAt: number): number {
  let pos = 0;
  for (let i = 0; i < blockIndex; i += 1) {
    pos += serializeMerkzettelMarkdown([blocks[i]]).length + 1;
  }
  const block = blocks[blockIndex];
  if (!block) return pos;
  if (block.kind === 'bullet') pos += 2;
  else if (block.kind === 'number') pos += `${block.n}. `.length;
  let seen = 0;
  for (const span of block.inlines) {
    const { open, close } = wrapLengths(span);
    if (visibleAt <= seen + span.text.length) return pos + open + (visibleAt - seen);
    pos += open + span.text.length + close;
    seen += span.text.length;
  }
  return pos;
}

/**
 * Neue Zeile an der Schreibmarke. Marker werden vor dem Umbruch geschlossen.
 * Laufende Schrift (fett, kursiv, durchstreichen, hervorheben) gilt auf der
 * neuen Zeile weiter, ohne dass `*`, `**`, `~~` oder `==` im Text stehen.
 * Aufzählung und Nummerierung setzen dieselbe Listenart fort.
 */
export function insertMerkzettelLineBreak(
  raw: string,
  caret: number,
  active?: MerkzettelArmedMark['kind'] | null,
): MerkzettelLineBreak {
  const text = String(raw ?? '').replace(/\r\n/g, '\n');
  const activeKind = active || null;
  const activeMarks = activeKind ? [activeKind] : [];
  if (!text) return { body: '\n', caret: 1, continueKind: activeKind, continueMarks: activeMarks };
  const blocks = viewBlocksAsModel(buildMerkzettelView(text));
  if (!blocks.length) return { body: '\n', caret: 1, continueKind: activeKind, continueMarks: activeMarks };
  const loc = merkzettelLocateOffset(text, Math.max(0, Math.min(caret, text.length)));
  const blockIndex = Math.max(0, Math.min(loc.block, blocks.length - 1));
  const block = blocks[blockIndex];
  const split = splitMerkzettelInlines(block.inlines, visibleOffsetInBlock(block, loc.inline, loc.char));
  const left = replaceBlockInlines(block, split.left);
  const right = replaceBlockInlines(block, split.right);
  let next = [...blocks.slice(0, blockIndex), left, right, ...blocks.slice(blockIndex + 1)];
  if (left.kind === 'number') next = renumberNumberRun(next, blockIndex);
  const rightEmpty = right.inlines.length === 0;
  let continueMarks: MerkzettelMark[] = [];
  if (rightEmpty && split.marksAt.length) continueMarks = split.marksAt;
  else if (rightEmpty && activeKind) continueMarks = [activeKind];
  const continueKind = continueMarks.length === 1 ? continueMarks[0] : null;
  const body = serializeMerkzettelMarkdown(next);
  return {
    body,
    caret: Math.min(caretInBlock(next, blockIndex + 1, 0), body.length),
    continueKind,
    continueMarks,
  };
}

/**
 * Ein Zeichen in ein laufendes Format. Listen, die schon stehen, bekommen nur den Buchstaben.
 * Sonst entsteht gültiges Allowlist-Markdown, nie eine offene Marker-Hälfte.
 */
export function merkzettelTypeIntoFormat(
  raw: string,
  caret: number,
  format: MerkzettelFormat | null,
  text: string,
): { body: string; caret: number } {
  const ch = String(text ?? '');
  const body = clipMerkzettelBody(raw);
  const at = Math.max(0, Math.min(body.length, Number.isFinite(caret) ? caret : 0));
  if (!format) {
    const next = clipMerkzettelBody(body.slice(0, at) + ch + body.slice(at));
    return { body: next, caret: Math.min(at + ch.length, next.length) };
  }
  if (format === 'bullet' || format === 'number') {
    const view = body ? buildMerkzettelView(body) : [];
    const loc = body ? merkzettelLocateOffset(body, at) : { block: 0, inline: -1, char: 0 };
    const block = view[loc.block];
    if (block && block.kind === format) {
      const next = clipMerkzettelBody(body.slice(0, at) + ch + body.slice(at));
      return { body: next, caret: Math.min(at + ch.length, next.length) };
    }
    const chunk = format === 'bullet' ? `- ${ch}` : `1. ${ch}`;
    const next = clipMerkzettelBody(body.slice(0, at) + chunk + body.slice(at));
    return { body: next, caret: Math.min(at + chunk.length, next.length) };
  }
  const [open, close] = INLINE_MARK[format];
  if (body.slice(at - open.length, at) === open && body.slice(at, at + close.length) === close) {
    const next = clipMerkzettelBody(body.slice(0, at) + ch + body.slice(at));
    return { body: next, caret: Math.min(at + ch.length, next.length) };
  }
  if (body) {
    const loc = merkzettelLocateOffset(body, at);
    const block = buildMerkzettelView(body)[loc.block];
    const span = block?.inlines[loc.inline];
    if (span && spanMarks(span).includes(format as MerkzettelMark)) {
      const mdAt = span.mdStart + Math.min(span.text.length, loc.char);
      const next = clipMerkzettelBody(body.slice(0, mdAt) + ch + body.slice(mdAt));
      return { body: next, caret: Math.min(mdAt + ch.length, next.length) };
    }
  }
  const chunk = `${open}${ch}${close}`;
  const next = clipMerkzettelBody(body.slice(0, at) + chunk + body.slice(at));
  return { body: next, caret: Math.min(at + open.length + ch.length, next.length) };
}

/** Klasse: Format an, Zeichen, Shift+Enter, weiteres Zeichen. Sichttext ohne Marker. */
export function merkzettelFormatShiftEnter(
  format: MerkzettelFormat,
  first: string,
  second: string,
): { markdown: string; visible: string } {
  const started = merkzettelTypeIntoFormat('', 0, format, first);
  const broken = insertMerkzettelLineBreak(started.body, started.caret, null);
  const nextFormat: MerkzettelFormat | null =
    broken.continueKind ||
    (broken.continueMarks.length === 1 ? broken.continueMarks[0] : null) ||
    (format === 'bullet' || format === 'number' ? format : null);
  const typed = merkzettelTypeIntoFormat(broken.body, broken.caret, nextFormat, second);
  const markdown = serializeMerkzettelMarkdown(parseMerkzettelMarkdown(typed.body));
  return { markdown, visible: merkzettelVisiblePlain(markdown) };
}

/**
 * Anzeige-Modell. `armed` ist nur die Schreibmarke (fett tippen), nicht gespeichertes Markdown.
 */
export function merkzettelEditorView(raw: string, armed: MerkzettelArmedMark | null): MerkzettelViewBlock[] {
  const text = String(raw ?? '').replace(/\r\n/g, '\n');
  const view = buildMerkzettelView(text);
  const blocks: MerkzettelViewBlock[] = view.length
    ? view.map((block) => ({ ...block, inlines: block.inlines.map((span) => ({ ...span })) }))
    : [{ kind: 'line', mdStart: 0, mdEnd: 0, contentStart: 0, inlines: [] }];
  if (!armed) return blocks;
  const at = Math.max(0, Math.min(armed.at, text.length));
  let host = blocks[blocks.length - 1];
  for (const block of blocks) {
    if (at >= block.mdStart && at <= block.mdEnd) {
      host = block;
      break;
    }
  }
  const armedMarks = canonicalMarks(armed.marks?.length ? armed.marks : [armed.kind]);
  const empty: MerkzettelInline = {
    kind: armed.kind,
    marks: armedMarks,
    text: '',
    mdStart: at,
    mdEnd: at,
  };
  if (!host.inlines.length) {
    host.inlines = [empty];
    return blocks;
  }
  const next: MerkzettelInline[] = [];
  let placed = false;
  for (const span of host.inlines) {
    if (placed || span.kind !== 'text' || at < span.mdStart || at > span.mdEnd) {
      next.push(span);
      continue;
    }
    const cut = at - span.mdStart;
    if (cut > 0) {
      next.push({
        kind: 'text',
        marks: [],
        text: span.text.slice(0, cut),
        mdStart: span.mdStart,
        mdEnd: span.mdStart + cut,
      });
    }
    next.push(empty);
    if (cut < span.text.length) {
      next.push({
        kind: 'text',
        marks: [],
        text: span.text.slice(cut),
        mdStart: span.mdStart + cut,
        mdEnd: span.mdEnd,
      });
    }
    placed = true;
  }
  if (!placed) next.push(empty);
  host.inlines = next;
  return blocks;
}

export type MerkzettelDomRow = {
  kind: 'line' | 'bullet' | 'number';
  n?: number;
  text: string;
};

/**
 * DOM-Zeilen aus dem Editor. Geklonte Listenpunkte tragen oft alle n=1
 * (Enter im flex-Block oder kopiertes `<li>`). Die Reihe wird 1, 2, 3.
 * Eine schon fortlaufende Reihe (2, 3) bleibt.
 */
export function merkzettelMarkdownFromDomRows(rows: MerkzettelDomRow[]): string {
  const copy = rows.map((row) => ({
    kind: row.kind,
    n: Number(row.n) || 0,
    text: String(row.text ?? ''),
  }));
  let i = 0;
  while (i < copy.length) {
    if (copy[i].kind !== 'number') {
      i += 1;
      continue;
    }
    let j = i;
    while (j < copy.length && copy[j].kind === 'number') j += 1;
    const ns = copy.slice(i, j).map((row) => row.n);
    let sequential = true;
    for (let k = 1; k < ns.length; k += 1) {
      if (ns[k] !== ns[k - 1] + 1) sequential = false;
    }
    if (!sequential || ns.some((n) => n < 1)) {
      for (let k = i; k < j; k += 1) copy[k].n = k - i + 1;
    }
    i = j;
  }
  return copy
    .map((row) => {
      if (row.kind === 'bullet') return `- ${row.text}`;
      if (row.kind === 'number') return `${row.n}. ${row.text}`;
      return row.text;
    })
    .join('\n');
}

function serializeMerkzettelInlines(spans: MerkzettelInline[]): string {
  return spans
    .map((span) => {
      const marks = spanMarks(span);
      if (!marks.length) return span.text;
      let inner = span.text;
      for (const mark of [...MERKZETTEL_MARK_ORDER].reverse()) {
        if (!marks.includes(mark)) continue;
        const [open, close] = INLINE_MARK[mark];
        inner = `${open}${inner}${close}`;
      }
      return inner;
    })
    .join('');
}

export function serializeMerkzettelMarkdown(blocks: MerkzettelBlock[]): string {
  return blocks
    .map((block) => {
      const inner = serializeMerkzettelInlines(block.inlines);
      if (block.kind === 'bullet') return `- ${inner}`;
      if (block.kind === 'number') return `${block.n}. ${inner}`;
      return inner;
    })
    .join('\n');
}

export function merkzettelMarkdownIsSafe(blocks: MerkzettelBlock[]): boolean {
  if (!Array.isArray(blocks)) return false;
  for (const block of blocks) {
    if (!block || !BLOCK_KINDS.has(block.kind) || !Array.isArray(block.inlines)) return false;
    for (const span of block.inlines) {
      if (!span || !INLINE_KINDS.has(span.kind) || typeof span.text !== 'string') return false;
    }
  }
  return true;
}

/**
 * List-Filter nur auf den eigenen Sitz — nie kanzleiweit.
 * Leere ID → leerer Filter (fail-closed, kein Fetch).
 */
export function buildMerkzettelPbFilter(customerId: string): string {
  const cid = sanitizeMerkzettelSeatId(customerId);
  if (!cid) return '';
  return `customer_id='${cid}'`;
}

export function merkzettelFilterIsKanzleiWide(filter: string): boolean {
  const f = String(filter || '').trim();
  if (!f) return false;
  if (/kanzlei_id\s*=/.test(f) && !/customer_id\s*=/.test(f)) return true;
  if (/\|\|/.test(f) && /kanzlei_id/.test(f)) return true;
  return false;
}

export function merkzettelRecordVisibleToSeat(
  record: Pick<MerkzettelRecord, 'customer_id'> | null | undefined,
  seatId: string,
): boolean {
  const cid = sanitizeMerkzettelSeatId(seatId);
  const rid = sanitizeMerkzettelSeatId(record?.customer_id);
  return Boolean(cid) && cid === rid;
}

export function assertOwnMerkzettelRecords(
  records: Array<Pick<MerkzettelRecord, 'customer_id'>>,
  seatId: string,
): void {
  for (const row of records) {
    if (!merkzettelRecordVisibleToSeat(row, seatId)) {
      throw new Error('MERKZETTEL_SEAT_OR_USER: fremde Notiz verworfen');
    }
  }
}

export function buildMerkzettelNcPath(args: {
  mandantenRoot?: string;
  kanzleiId: string;
  customerId: string;
}): string {
  const root = String(args.mandantenRoot || 'Mandanten').replace(/^\/+|\/+$/g, '');
  const kid = sanitizeMerkzettelSeatId(args.kanzleiId);
  const cid = sanitizeMerkzettelSeatId(args.customerId);
  if (!root || !kid || !cid) return '';
  return `/${root}/${kid}/sitze/${cid}/${MERKZETTEL_FOLDER}/${MERKZETTEL_FILENAME}`;
}

export function merkzettelNcPathOwnedBySeat(path: string, customerId: string): boolean {
  const cid = sanitizeMerkzettelSeatId(customerId);
  if (!cid) return false;
  const norm = String(path || '')
    .trim()
    .replace(/\\/g, '/')
    .replace(/\/+/g, '/');
  if (!norm) return false;
  const needle = `/sitze/${cid}/${MERKZETTEL_FOLDER}/${MERKZETTEL_FILENAME}`;
  if (!norm.endsWith(needle) && !norm.endsWith(`${needle}`)) return false;
  if (/\/sitze\/[^/]+\/sitze\//.test(norm)) return false;
  const sitze = norm.match(/\/sitze\/([^/]+)\//);
  return Boolean(sitze && sitze[1] === cid);
}

/**
 * Das Portal hat keine Browser-WebDAV-Session (NC läuft über n8n).
 * Echte Session nur, wenn der Caller sie explizit setzt — sonst PB.
 */
export function hasMerkzettelNcWebdavSession(session?: { active?: boolean } | null): boolean {
  return session?.active === true;
}

export function selectMerkzettelBackend(opts?: {
  ncWebdavSession?: { active?: boolean } | null;
}): MerkzettelBackend {
  return hasMerkzettelNcWebdavSession(opts?.ncWebdavSession) ? 'nc' : 'pb';
}

export function merkzettelPersistHint(backend: MerkzettelBackend): string {
  if (backend === 'nc') {
    return 'Gespeichert in Ihrem Dokumentenordner';
  }
  return MERKZETTEL_HANDY_SYNC_LATER;
}

export function merkzettelLocalStorageIsSoleStore(source: string): boolean {
  const s = String(source || '');
  const writesLs = /localStorage\.setItem\s*\(/.test(s);
  const writesPb = /portal_merkzettel/.test(s) || /collections\/portal_merkzettel/.test(s);
  const writesNc = /Notizen\/merkzettel\.txt/.test(s) || /MERKZETTEL_FOLDER/.test(s);
  return writesLs && !writesPb && !writesNc;
}
